/**
 * Parameters for building an excluded entities query
 */
export interface BuildExcludedEntitiesQueryParams {
  /** The entity name (IP address, domain, etc.) */
  entityName: string;
  /** The entity type (IP, DOMAIN, IP_RANGE, etc.) */
  entityType: string;
  /** The tenant ID */
  tenantId: string;
  /** Optional content/alert ID for detection-specific queries */
  contentId?: string;
}

/**
 * The structure of excluded entities query variables for GraphQL
 */
export interface ExcludedEntitiesQueryVariables {
  query: string;
  offset: number;
  size: number;
  sort: Array<{ sort_by: string; sort_dir: string }>;
}

/**
 * Builds Elasticsearch query variables for querying excluded entities.
 *
 * This function generates the appropriate Elasticsearch query string based on:
 * - Entity type (DOMAIN vs IP/IP_RANGE)
 * - Whether a content_id is provided (detection-specific vs global queries)
 *
 * @param params - The entity information to build the query for
 * @returns Query variables object, or undefined if required fields are missing
 *
 * @example
 * ```typescript
 * // For a domain entity with detection context
 * const vars = buildExcludedEntitiesQuery({
 *   entityName: 'example.com',
 *   entityType: 'DOMAIN',
 *   tenantId: 'tenant-123',
 *   contentId: 'alert-456',
 * });
 *
 * // For an IP entity without detection context (global query)
 * const vars = buildExcludedEntitiesQuery({
 *   entityName: '192.168.1.1',
 *   entityType: 'IP',
 *   tenantId: 'tenant-123',
 * });
 * ```
 */
export const buildExcludedEntitiesQuery = ({
  entityName,
  entityType,
  tenantId,
  contentId,
}: BuildExcludedEntitiesQueryParams):
  | ExcludedEntitiesQueryVariables
  | undefined => {
  // Validate required fields
  if (!entityName || !entityType || !tenantId) {
    return undefined;
  }

  // Build tenant filter
  const tenantQuery = `,{"terms":{"tenant":["${tenantId}"]}}`;

  // Build the appropriate query based on entity type and whether we have a content_id
  // Content ID queries are detection-specific, without content_id are global
  const query = contentId
    ? // Detection-specific queries (with content_id)
      entityType === 'DOMAIN'
      ? // Domain query: match by name exactly
        `{"query": {"bool": {"must": [{"match": {"content_id": "${contentId}"}}, {"term": {"entity_name.keyword": "${entityName}"}}, {"term": {"entity_type": "${entityType}"}}${tenantQuery}]}}}`
      : // IP/IP_RANGE query: match by IP or IP range
        `{"query": {"bool": {"must": [{"match": {"content_id": "${contentId}"}}, {"term": {"entity_type": "${entityType}"}}, {"bool": {"should": [{"term": {"entity_ip": "${entityName}"}}, {"range": {"entity_ip_range": {"from": "${entityName}", "to": "${entityName}"}}}]}}${tenantQuery}]}}}`
    : // Global queries (without content_id)
      entityType === 'DOMAIN'
      ? // Domain query: match by name exactly
        `{"query": {"bool": {"must": [{"term": {"entity_name.keyword": "${entityName}"}}, {"term": {"entity_type": "${entityType}"}}${tenantQuery}]}}}`
      : // IP/IP_RANGE query: match by IP or IP range
        `{"query": {"bool": {"must": [{"term": {"entity_type": "${entityType}"}}, {"bool": {"should": [{"term": {"entity_ip": "${entityName}"}}, {"range": {"entity_ip_range": {"from": "${entityName}", "to": "${entityName}"}}}]}}${tenantQuery}]}}}`;

  return {
    query,
    offset: 0,
    size: 10,
    sort: [{ sort_by: 'entity_name.keyword', sort_dir: 'desc' }],
  };
};
